import { useAuth } from 'context/AuthContext';

/**
 * usePermission — granular permission checks
 *
 * Usage:
 *   const { can, canAny, canAll, hasRole, isAdmin } = usePermission();
 *   if (can('certificates.approve_qs')) { ... }
 */
export const usePermission = () => {
    const { user, can, canAny, canAll, isAdmin } = useAuth();

    const roles = user?.roles?.map((r) => r.name) ?? [];

    /** Check a single permission string e.g. "projects.create" */
    const hasPermission = (permission) => {
        if (isAdmin) return true;
        return can(permission);
    };

    /** Check if user holds at least one of the given roles */
    const hasRole = (roleOrRoles) => {
        const list = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
        return list.some((r) => roles.includes(r));
    };

    /** Check if user holds ALL of the given roles */
    const hasAllRoles = (roleList) => roleList.every((r) => roles.includes(r));

    /** Check any of a permission list */
    const hasAnyPermission = (perms) => {
        if (isAdmin) return true;
        return canAny(perms);
    };

    /** Check all of a permission list */
    const hasAllPermissions = (perms) => {
        if (isAdmin) return true;
        return canAll(perms);
    };

    return {
        can: hasPermission,
        canAny: hasAnyPermission,
        canAll: hasAllPermissions,
        hasRole,
        hasAllRoles,
        isAdmin,
        roles,
        permissions: user?.permissions ?? []
    };
};
